import { CURRENCY_SYMBOL, formatPrice, type Currency } from '@wishare/shared';

/** Мелкие помощники представления — всё, что превращает данные в текст для глаз. */

export { formatPrice, CURRENCY_SYMBOL };

/** Инициалы для аватара: «Даритель Маша» → «ДМ». */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || '?';
}

/** «22 июля», «22 июля 2027» — год показываем только если он не текущий. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/**
 * «2026-08-02» — отметка в календаре, а не мгновение времени: день рождения
 * остаётся вторым августа и в Калининграде, и во Владивостоке. new Date()
 * разобрал бы такую строку как полночь по Гринвичу, и западнее нулевого
 * меридиана сегодняшнее событие показывалось бы вчерашним. Строки, где время
 * всё-таки указано, разбираем как есть.
 */
function parseCalendarDate(iso: string): Date {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!parts) return new Date(iso);
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/** «через 12 дней», «сегодня», «3 дня назад» — человеку так понятнее, чем дата. */
export function formatRelativeDays(iso: string | null | undefined): string {
  if (!iso) return '';
  const target = parseCalendarDate(iso);
  if (Number.isNaN(target.getTime())) return '';

  const startOfDay = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  const days = Math.round((startOfDay(target) - startOfDay(new Date())) / 86_400_000);

  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  if (days === -1) return 'вчера';

  const formatter = new Intl.RelativeTimeFormat('ru-RU', { numeric: 'auto' });
  return formatter.format(days, 'day');
}

/** Домен для подписи «откуда»: https://www.ozon.ru/product/… → ozon.ru */
export function hostOf(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function faviconFor(url: string | null | undefined): string | null {
  const host = hostOf(url);
  return host ? `https://favicon.yandex.net/favicon/v2/${host}?size=32` : null;
}

/**
 * Склонение существительных по числу: 1 желание, 2 желания, 5 желаний.
 * Без этого интерфейс всё время говорит «5 желание» и выглядит машинным.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(count) % 100;
  const mod10 = mod100 % 10;

  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export function pluralize(count: number, one: string, few: string, many: string): string {
  return `${count} ${plural(count, one, few, many)}`;
}

/** Цена из поля ввода («12 490,50») в копейки. */
export function priceInputToMinor(value: string): number | null {
  const cleaned = value.replace(/[^\d.,]/g, '').replace(',', '.');
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

/** Копейки обратно в текст для поля ввода — без валютного символа. */
export function minorToPriceInput(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return '';
  const major = minor / 100;
  return Number.isInteger(major) ? String(major) : major.toFixed(2);
}

export function currencyLabel(currency: Currency | string | null | undefined): string {
  if (!currency) return '';
  return CURRENCY_SYMBOL[currency as Currency] ?? currency;
}
